import { TICK_RATE, ticks } from './config';
import { CreepKind } from './creeps';
import { BossKind } from './economy';

/**
 * the battle royale (ADR-0120): a hundred gardeners on one fixed map, the last team standing wins, the Frost
 * closing in on them. Its tuning lives here; its sim is `systems/authority/royale.ts`.
 */

/**
 * Its map (ADR-0120): the same every game, so players learn it. Village (ADR-0188, drawn in Cainos' packs) since
 * ADR-0190; Bramblewick before.
 */
export const ROYALE_MAP_ID = 'village';

/** Maps a battle royale can be played on: its own, and Bramblewick, the one before (`?royale&map=bramblewick`, dev only). */
export const ROYALE_MAPS: readonly string[] = [ROYALE_MAP_ID, 'bramblewick', 'proving-ground'];

/**
 * Its line-ups (ADR-0120 amended, ADR-0207): teams of one to five, a party's size (the party ADR's most), a hundred
 * gardeners in every one, as the user set — a hundred of one, fifty of two, thirty-three of three (ninety-nine), twenty-five
 * of four, twenty of five. A battle royale's teams are squads, numbered 1 … `teams` (`squads`), never Blue and Red: the
 * client draws them by relation (yours and everyone else's).
 */
export const ROYALE_FORMATS = {
  solo: { teams: 100, teamSize: 1, name: 'Solo' },
  duo: { teams: 50, teamSize: 2, name: 'Duos' },
  trio: { teams: 33, teamSize: 3, name: 'Trios' },
  quad: { teams: 25, teamSize: 4, name: 'Quads' },
  squad: { teams: 20, teamSize: 5, name: 'Squads' },
} as const;

export type RoyaleFormat = keyof typeof ROYALE_FORMATS;

export const isRoyaleFormat = (s: string): s is RoyaleFormat => Object.hasOwn(ROYALE_FORMATS, s);

/** The format a party of `size` plays (its squad): solo alone, duos for two … squads for five; none past that. */
export const royaleFormatOf = (size: number): RoyaleFormat | undefined =>
  (Object.keys(ROYALE_FORMATS) as RoyaleFormat[]).find((f) => ROYALE_FORMATS[f].teamSize === size);

/** The teams of a battle royale of `n` squads: 1 … n. */
export const squads = (n: number): number[] => Array.from({ length: n }, (_, i) => i + 1);

/**
 * How well the `i`th bot a battle royale seats plays (a rating on the ladder's curve, ADR-0120): spread from 200 to 1100,
 * the same field every game, in the browser and in a room alike.
 */
export const royaleBotRating = (i: number): number => 200 + ((i * 131) % 900);

/**
 * One phase of the Frost (ADR-0120): the next circle shows for `waitTicks`, then the Frost closes in on it over
 * `shrinkTicks`. From the phase's start on, standing outside the circle bites `dps` a second (`ROYALE.beatTicks` at a
 * time). The last phase closes to nothing, so every round ends.
 */
export interface ZonePhase {
  /** The radius it closes to (px). */
  readonly radius: number;
  readonly waitTicks: number;
  readonly shrinkTicks: number;
  readonly dps: number;
}

/** What a roulette pocket does (ADR-0232, `ROYALE.roulette.pockets`): costs health, or drops an Uncommon or Epic gun. */
export const Pocket = { Hurt: 0, Uncommon: 1, Epic: 2 } as const;
export type Pocket = (typeof Pocket)[keyof typeof Pocket];
const { Hurt: H, Uncommon: U, Epic: E } = Pocket;

export const ROYALE = {
  /** A gardener's health in a battle royale: 100, and up to `PICKUP.shieldMax` of shield on top of it. */
  health: 100,
  /**
   * How far a battle royale's maps' sight cone reaches (px, `MapDef.cone`, written by their map makers): half again
   * `VISION.coneRange`, at the user's ask (ADR-0192 amended). Its bubble round you stays `VISION.radius`.
   */
  cone: 225,
  /**
   * How a gardener's sight turns after its aim (ADR-0010 amended; the gun aims at once): freely while an unseen stamina
   * lasts (`stamina` radians of turning past `turn` a tick: a flick round or two), then no quicker than `turn` a tick (half
   * a turn a second); `rest` a tick comes back while the cone keeps up with the aim (empty to full in two seconds). So a
   * flick shows what is behind you, and whipping the mouse round doesn't sweep the fog off everything round you. In a bush
   * it always turns slowly: `bush` a tick, no stamina.
   */
  sight: {
    turn: Math.PI / TICK_RATE,
    stamina: 2 * Math.PI,
    rest: (2 * Math.PI) / ticks(2),
    bush: Math.PI / 2 / TICK_RATE,
  },
  /**
   * A bush to hide in (ADR-0193: content `ThingKind.hide`): standing still for `stillTicks` within `reach` px of its foot
   * hops in (onto it, ADR-0193 amended). Inside, a gardener peers out through the leaves (`eye`, ADR-0193 amended): a narrow
   * slit of sight where they aim, as long as the map's cone, and a small bubble round them; another squad sees them only
   * from within `reveal` px; moving, shooting, throwing or being hurt brings them out.
   */
  bush: { reach: 16, stillTicks: ticks(0.5), reveal: 26, eye: { radius: 30, coneHalfAngle: 0.17, coneSoftness: 0.12 } },
  /**
   * The underground's stairs (ADR-0196 amended, `MapDef.stairs`), walked, never a key: a flight is `width` px across, and
   * where one comes out of it is `spot` px off its steps. A gardener whose middle comes `down` px south of where one comes
   * out of it up top (down its stairwell's steps), or `up` px north of where one comes out of it below (up its steps), is
   * taken to the other end, where walking on takes them off the steps. Only within `deep` px past that line: what lies
   * beyond is no flight's.
   */
  stairs: { width: 32, spot: 7, down: 24, up: 40, deep: 16 },
  /**
   * The crypts (ADR-0202, ADR-0215: `MapDef.crypts`): their doorways (`cryptDoorAt`) `width` px across, a gardener `deep`
   * px into one taken through — in from the hall only with its squad holding the key (any crypt's), out again always.
   * Each round each one's `guns` weapon altars hold that many guns drawn from every tier's but Common's, and its
   * `explosives` explosives' spots that many kinds of explosive, each as likely, none twice (ADR-0202 amended, sim
   * `cryptStock`); none of it runs out (`Crypt.loot`: a big heal of `heal`, a shield of `shield`, a full slot of an
   * explosive), grown back the tick after it is taken.
   */
  crypt: { width: 24, deep: 9, heal: 50, shield: 50, guns: 3, explosives: 2 },
  /**
   * The underground's Frost (ADR-0196 amended): no circle down there. Once the circle of phase `phase` (`zone` index: the
   * second) starts closing in, the Frost pours down every stairwell whose top it covers (each as it comes to it) and
   * creeps from its foot along the tunnels at `speed` px a second (a fifth of a walk: outrun, never outwaited); where it
   * lies it bites as up top, the phase's `dps`.
   */
  flood: { phase: 1, speed: 14 },
  /**
   * The Frost (ADR-0120): winter closing in on the garden in six circles, each inside the last (where nobody knows until
   * it shows: the world's secret). The first circle shows as the round starts, while the whole map is still green. Every
   * circle after it is about half the last. The waits shrink fivefold over the round and the edge keeps its pace (about
   * 15 px/s from the third circle on, at any map scale; it slowed to 3 px/s before), so the late game has no lulls
   * (ADR-0120 amended). Times are a 128-cell map's and `MapDef.scale` multiplies them: on Village (×3) the Frost sets out
   * 30 s in and the last circle closes 5:12 in (was 1:00 and 10:57).
   */
  zone: [
    { radius: 640, waitTicks: ticks(10), shrinkTicks: ticks(20), dps: 1 },
    { radius: 340, waitTicks: ticks(13), shrinkTicks: ticks(15), dps: 2 },
    { radius: 190, waitTicks: ticks(10), shrinkTicks: ticks(10), dps: 4 },
    { radius: 100, waitTicks: ticks(7), shrinkTicks: ticks(6), dps: 7 },
    { radius: 40, waitTicks: ticks(4), shrinkTicks: ticks(4), dps: 10 },
    { radius: 0, waitTicks: ticks(2), shrinkTicks: ticks(3), dps: 15 },
  ] as readonly ZonePhase[],
  /** The Frost bites once this often (its phase's `dps` × this, in seconds). */
  beatTicks: ticks(1),
  /**
   * A next circle's centre lies at most this share of the room between the two circles from the current one's centre,
   * so the circle that follows never sits right at the edge of the last.
   */
  drift: 0.85,
  /** Mates start this far apart beside their team's spawn (px), in a row either side of it. */
  mateGap: 22,
  /**
   * In the browser, the round starts this long after you are in (a lobby, ADR-0015: nothing moves until then); a room's
   * wait ends on as long a GET READY in the game (the waiting screen goes).
   */
  startTicks: ticks(3),
  /**
   * A room on the server (ADR-0120 amended): it takes the parties that ask for a game (a party of N plays the format of N,
   * `royaleFormatOf`) for `joinMs` after it opens, and its round starts `waitTicks` after its first player is in (sooner once every seat is
   * a player's); the squads nobody took are filled with bots as it starts.
   */
  room: { joinMs: 5000, waitTicks: ticks(8) },
  /**
   * The creatures a battle royale's camps grow (and grow back as, another of them each time, ADR-0026): the ones whose
   * prize is for you — Mint Rush, Chili Rage, a Heart Berry, an Leaf Shield, a Lantern's vision ward. No Hedgehog (it
   * gives nothing) and no Magpies (they steal gold nobody spends).
   */
  camps: [CreepKind.Snail, CreepKind.Wasp, CreepKind.Toad, CreepKind.Shroom, CreepKind.Moth] as readonly number[],
  /**
   * Down but not out (squads of two or more, ADR-0120 step 4): a gardener whose health runs out while a mate still stands goes down
   * instead: `hp` to bleed (`bleed` a second, about 30 s), crawling at `crawl` of walking pace, no gun. Their mates pick
   * them up as a Zombie Nights party does (ADR-0120 amended: standing in `REVIVE.radius`), in `reviveTicks` (each mate
   * there one more tick of it a tick: two get them up twice as fast), with `reviveHp` (feedback #26 wanted the revived to
   * get more than a sliver back). Just down, no blow touches them for `graceTicks` (a gold dome over them; the Frost and
   * the bleed do); after, a blow lands on them `hurt` as hard.
   */
  down: { hp: 90, bleed: 3, crawl: 0.35, reviveTicks: ticks(4), reviveHp: 60, graceTicks: ticks(1), hurt: 0.7 },
  /**
   * Seeds and seedbeds (feedback #27, a garden's take on Fortnite's reboot cards): a gardener who dies while a mate of
   * theirs still stands leaves a seed where they fell, for their squad alone. It lies `groundTicks` (then it withers)
   * until a mate walks within `reach` px of it, who carries it for good (it falls again where they die). Standing within
   * `bedReach` px of a seedbed (the map's, `MapDef.seedbeds`) for `plantTicks` plants every seed they carry, no key held
   * (ADR-0120 amended): each of those gardeners grows back beside the bed, whole, with the Pistol. Each mate of theirs
   * standing at the bed too plants it a tick more a tick: two at the bed grow them back twice as fast.
   */
  seed: { groundTicks: ticks(90), reach: 14, bedReach: 30, plantTicks: ticks(5) },
  /**
   * A gun's hit on a gardener does this share of its damage (ADR-0189): a fight lasts long enough to take cover, to heal
   * and for a shield to matter. Not the Frost's bite, nor a creature's.
   */
  damage: 0.8,
  /**
   * The stone altars (ADR-0189, amended by ADR-0202: `MapDef.altars`): a gardener standing within `reach` px of one's
   * middle for `wakeTicks` (its four runes light one by one; nobody there, the light fades twice as fast) wakes it, and
   * `riseTicks` later its guardian rises out of it — a Classic boss, `bosses[i]` for altar `i` (the Pumpkin King, the
   * Snapdragon) —, after whoever is near, never past `leash` px of its altar. Bring it down within `ticks` of the waking
   * and it drops an Epic gun where it fell (ADR-0215: one of `ROYALE_TIERS.epic`, ADR-0216, the world's secret says which)
   * for the squad of whoever the last hit counts for. Nobody standing within `away` px of the altar (room to keep a gun's
   * range off a guardian at its leash's end), or the time up, and it sinks back into the altar, which sleeps `restTicks`
   * before it can be woken again. Won, it is spent for the round.
   */
  altar: {
    reach: 22,
    wakeTicks: ticks(2),
    riseTicks: ticks(1.6),
    leash: 220,
    away: 380,
    ticks: ticks(90),
    restTicks: ticks(25),
    bosses: [BossKind.Pumpkin, BossKind.Snapdragon] as readonly number[],
  },
  /**
   * The crypt key (ADR-0202), in a hot-air balloon's basket (ADR-0215; a stone altar's prize before): its holder's squad
   * may go into any of the crypts (`MapDef.crypts`). It lies in the opened basket, and its holder knocked out, where they
   * fell (in a crypt: outside its door), for whoever walks over it with none of their own (`PickupKind.Key`).
   */
  key: { amount: 1 },
  /**
   * MOST WANTED (ADR-0229): the map's wanted boards (`MapDef.posters`), `lit` of them drawn each round with a fresh
   * poster. A standing gardener within `reach` px of a lit one's foot presses SPACE and takes its bounty, the poster
   * coming off the board for the round: for `ticks` everyone sees them on the map. Still standing at the end, a gold chest
   * lands at their feet (`CrateKind.Gold`, opened as a chest is: an Epic gun, `heal` health and `shield` of shield);
   * knocked out first, it lands where they fell, for whoever gets there. One bounty at a time a gardener.
   */
  wanted: { lit: 3, reach: 30, ticks: ticks(20), heal: 50, shield: 50 },
  /**
   * The casino's roulette (ADR-0232): the map's roulette tables (`MapDef.roulettes`, each one's foot: the middle of its
   * box's bottom edge, its box `size` px, w × h, what stops feet and shots). A standing gardener within `reach` px of a
   * free table's box presses SPACE and spins it, as often as they dare (ADR-0232 amended); the ball settles `ticks` later
   * in one of its `pockets` (`Pocket`, round the wheel from its top clockwise), drawn from the round's hidden seed: half of
   * them cost the spinner `hurt` health past any shield or guard (the house always gets paid: it may put them down or out,
   * `THE_HOUSE`); the rest drop a mystery gun of their tier beside the spinner for their squad, as a chest's is.
   */
  roulette: {
    reach: 14,
    size: [48, 40],
    ticks: ticks(3.4),
    hurt: 50,
    pockets: [H, U, H, E, H, U, H, U, H, E, H, U, H, U, H, E],
  },
  /**
   * Its chests and mini chests open by standing at them, never by shots (ADR-0190): a standing gardener within `reach` px
   * of one fills its lock over `ticks`, a chest's, a mini chest's and a balloon's basket's alike; with nobody there it
   * empties twice as fast. Full, it opens for the nearest of them: a chest drops its gun for their squad, a mini chest
   * bursts.
   */
  open: { reach: 20, ticks: ticks(0.8) },
  /**
   * The lobby (ADR-0190 amended): while a room waits for its round, its gardeners warm up together on the map's lobby
   * island (`lobbyOf`; without one within `radius` px of the map's middle) with the battle royale's Pistol. Its weapon
   * altars hold the chests' guns for anyone to take, again and again (`MapDef.gunAltars`); its dummies take shots, and
   * nobody can hurt a gardener: shots fly through them. Nothing of the map proper breaks or opens before the round. In
   * the browser it lasts `ticks` (ENTER starts it sooner).
   */
  lobby: { radius: 96, ticks: ticks(20) },
} as const;
